import { Minus, Plus, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cellIcon, formatMoney, gridPosition, GROUP_COLORS } from '../utils/board.js';
import { TokenSprite } from './TokenSprite.jsx';
import { Dice, DICE_ROLL_DURATION } from './Dice.jsx';

const CORNER_ART = {
  0: '/assets/board-corner-start.webp',
  10: '/assets/board-corner-jail.webp',
  20: '/assets/board-corner-parking.webp',
  30: '/assets/board-corner-go-to-jail.webp',
};

function incomeData(cell, property, game) {
  if (!property || !cell.rents?.length) return null;
  const related = cell.type === 'street'
    ? game.board.filter((candidate) => candidate.type === 'street' && candidate.group === cell.group)
    : game.board.filter((candidate) => candidate.type === cell.type);
  const ownedCount = property.ownerId
    ? related.filter((candidate) => game.properties.find((item) => item.id === candidate.index)?.ownerId === property.ownerId).length
    : 0;
  if (cell.type === 'utility') {
    return { active: ownedCount, values: cell.rents.map((value, index) => ({ level: index + 1, label: `${value}×` })) };
  }
  const values = cell.type === 'transport'
    ? cell.rents.slice(0, related.length)
    : related.map((_, index) => (index === related.length - 1 ? cell.rents[0] * 2 : cell.rents[0]));
  return { active: ownedCount, values: values.map((value, index) => ({ level: index + 1, label: formatMoney(value) })) };
}

function atlasPosition(index) {
  const column = index % 7;
  const row = Math.floor(index / 7);
  return `${(column / 6) * 100}% ${(row / 3) * 100}%`;
}

function Cell({ cell, property, owner, selected, onSelect, atlasIndex, income }) {
  const position = gridPosition(cell.index);
  const isCorner = [0, 10, 20, 30].includes(cell.index);
  const hasGroup = cell.group && GROUP_COLORS[cell.group];
  return (
    <button
      type="button"
      className={`board-cell board-cell--${cell.type} ${isCorner ? 'board-cell--corner' : ''} ${position.row === 11 ? 'board-cell--bottom' : ''} ${!isCorner && [1, 11].includes(position.column) ? 'board-cell--side' : ''} ${selected ? 'is-selected' : ''}`}
      style={{
        gridRow: position.row,
        gridColumn: position.column,
        '--group-color': hasGroup ? GROUP_COLORS[cell.group] : 'transparent',
        '--owner-color': owner?.color ?? 'transparent',
      }}
      onClick={() => onSelect(cell.index)}
      aria-label={`${cell.index}. ${cell.name}${cell.price ? `, стоимость ${cell.price}` : ''}`}
    >
      {hasGroup && <span className="board-cell__group" />}
      {isCorner && <img className="board-cell__corner-art" src={CORNER_ART[cell.index]} alt="" draggable="false" />}
      <span className="board-cell__number">{cell.index}</span>
      {property ? <span className="board-cell__art" aria-hidden="true" style={{ backgroundPosition: atlasPosition(atlasIndex) }} /> : !isCorner && cellIcon(cell.type) && <span className="board-cell__icon" aria-hidden="true">{cellIcon(cell.type)}</span>}
      <strong>{cell.name}</strong>
      {cell.price && <small>{formatMoney(cell.price)}</small>}
      {income && (
        <span className="income-tiers" aria-label={`Доход по уровню владения: ${income.values.map(({ level, label }) => `${level}: ${label}`).join(', ')}`}>
          {income.values.map(({ level, label }) => <i key={level} className={income.active === level ? 'is-active' : ''}>{label}</i>)}
        </span>
      )}
      {property?.ownerId && <span className="owner-mark" title={`Владелец: ${owner?.name ?? ''}`} />}
      {property?.mortgaged && <span className="mortgage-mark">ИПОТЕКА</span>}
      {property?.houses > 0 && (
        <span className={`buildings buildings--${property.houses === 5 ? 'hotel' : 'houses'}`} aria-label={property.houses === 5 ? 'Отель' : `Домов: ${property.houses}`}>
          {property.houses === 5 ? '◆' : '⌂'.repeat(property.houses)}
        </span>
      )}
    </button>
  );
}

export function Board({ game, selectedCell, onSelectCell, snapshotEpoch = 0 }) {
  const [zoom, setZoom] = useState(1);
  const [visiblePositions, setVisiblePositions] = useState(() => Object.fromEntries(game.players.map((player) => [player.id, player.position])));
  const observedRef = useRef({ gameId: game.id, sequence: game.lastMove?.sequence ?? 0, epoch: snapshotEpoch, positions: Object.fromEntries(game.players.map(player => [player.id, player.position])) });
  const animationRef = useRef(null);
  const [snapshotDice, setSnapshotDice] = useState({ epoch: snapshotEpoch, sequence: game.dice.sequence });
  const gameId = game.id;
  const diceSequence = game.dice.sequence;
  const moveSequence = game.lastMove?.sequence ?? 0;
  const movePlayerId = game.lastMove?.playerId ?? '';
  const moveFrom = game.lastMove?.from;
  const moveTo = game.lastMove?.to;
  const movePathKey = (game.lastMove?.path ?? []).join(',');
  const positionsKey = JSON.stringify(game.players.map(player => [player.id, player.position, player.bankrupt]));

  useEffect(() => {
    const entries = JSON.parse(positionsKey);
    const positions = Object.fromEntries(entries.map(([id, position]) => [id, position]));
    const previous = observedRef.current;
    const path = movePathKey ? movePathKey.split(',').map(Number) : [];
    const resync = previous.gameId !== gameId || previous.epoch !== snapshotEpoch;
    const newMove = previous.sequence !== moveSequence;
    const pathMatches = path.length > 0 && path.at(-1) === moveTo && positions[movePlayerId] === moveTo
      && !entries.find(([id]) => id === movePlayerId)?.[2];
    let animation = animationRef.current;
    const validAnimation = animation && animation.sequence === moveSequence && animation.playerId === movePlayerId
      && animation.pathKey === movePathKey && pathMatches;
    if (resync || newMove || (animation && !validAnimation)) {
      animation?.timers.forEach(timer => window.clearTimeout(timer));
      animationRef.current = null;
      animation = null;
    }
    const animateNewMove = newMove && !resync && moveSequence === previous.sequence + 1
      && previous.positions[movePlayerId] === moveFrom && pathMatches;
    if (animateNewMove) {
      animation = { sequence: moveSequence, playerId: movePlayerId, pathKey: movePathKey, position: moveFrom, timers: [] };
      animationRef.current = animation;
      const running = animation;
      running.timers = path.map((position, index) => window.setTimeout(() => {
        if (animationRef.current !== running) return;
        running.position = position;
        setVisiblePositions(current => ({ ...current, [movePlayerId]: position }));
        if (index === path.length - 1) animationRef.current = null;
      }, DICE_ROLL_DURATION + index * 190));
    }
    // Every other token immediately follows authority. Preserve only a valid,
    // currently running path instead of replaying missed moves after a resume.
    const reconciled = { ...positions };
    if (animation) reconciled[animation.playerId] = animation.position;
    const frame = window.requestAnimationFrame(() => {
      setVisiblePositions(reconciled);
      if (resync || (newMove && !animateNewMove)) setSnapshotDice({ epoch: snapshotEpoch, sequence: diceSequence });
    });
    observedRef.current = { gameId, sequence: moveSequence, epoch: snapshotEpoch, positions };
    return () => window.cancelAnimationFrame(frame);
  }, [gameId, diceSequence, movePathKey, movePlayerId, moveSequence, moveFrom, moveTo, positionsKey, snapshotEpoch]);

  useEffect(() => () => {
    animationRef.current?.timers.forEach(timer => window.clearTimeout(timer));
  }, []);

  const animateDice = snapshotEpoch === snapshotDice.epoch && game.dice.sequence > snapshotDice.sequence;

  const activePlayers = game.players.filter((player) => !player.bankrupt);
  return (
    <section className="board-section" aria-label="Игровое поле">
      <div className="board-toolbar" aria-label="Масштаб поля">
        <button type="button" onClick={() => setZoom((value) => Math.max(0.85, value - 0.15))} aria-label="Уменьшить"><Minus size={16} /></button>
        <button type="button" onClick={() => setZoom(1)} aria-label="Сбросить масштаб"><RotateCcw size={15} /></button>
        <button type="button" onClick={() => setZoom((value) => Math.min(1.6, value + 0.15))} aria-label="Увеличить"><Plus size={16} /></button>
      </div>
      <div className="board-scroll">
        <div className="game-board" style={{ width: `${zoom * 100}%` }}>
          {game.board.map((cell) => {
            const property = game.properties.find((item) => item.id === cell.index);
            const owner = property?.ownerId ? game.players.find((player) => player.id === property.ownerId) : null;
            const atlasIndex = property ? game.properties.findIndex((item) => item.id === cell.index) : -1;
            return <Cell key={cell.index} cell={cell} property={property} owner={owner} selected={selectedCell === cell.index} onSelect={onSelectCell} atlasIndex={atlasIndex} income={incomeData(cell, property, game)} />;
          })}

          <div className="board-center">
            <img className="board-center__map" src="/assets/board-city-isometric.webp" alt="" draggable="false" />
            <div className="board-center__brand">
              <span className="mini-skyline" aria-hidden="true"><i /><i /><i /><i /></span>
              <strong>Капиталия</strong>
              <span>город в твоих решениях</span>
            </div>
            <div className="board-center__status">
              <span>Сейчас ходит</span>
              <strong style={{ color: game.currentPlayer?.color }}>{game.currentPlayer?.name}</strong>
            </div>
            <Dice key={`${snapshotEpoch}:${game.dice.sequence}:${animateDice}`} dice={game.dice} board animate={animateDice} />
            <ol className="board-live-log" aria-label="Последние события" aria-live="polite" aria-atomic="true">
              {game.log.slice(-2).map((entry) => <li key={entry.id}>{entry.message}</li>)}
            </ol>
          </div>

          <div className="token-layer" aria-label="Фишки игроков">
            {activePlayers.map((player) => {
              const visiblePosition = visiblePositions[player.id] ?? player.position;
              const position = gridPosition(visiblePosition);
              const peers = activePlayers.filter((candidate) => (visiblePositions[candidate.id] ?? candidate.position) === visiblePosition);
              const peerIndex = peers.findIndex((candidate) => candidate.id === player.id);
              return (
                <TokenSprite
                  key={player.id}
                  token={player.token}
                  size="board"
                  title={`${player.name}, клетка ${visiblePosition}`}
                  className={player.id === game.currentPlayerId ? 'is-current' : ''}
                  data-position={visiblePosition}
                  data-player={player.id}
                  style={{
                    '--token-x': `${((position.column === 1 ? 0.6 : position.column === 11 ? 10.8 : position.column - 0.3) / 11.4) * 100}%`,
                    '--token-y': `${((position.row === 1 ? 1.05 : position.row === 11 ? 12.15 : position.row + 0.6) / 13.2) * 100}%`,
                    '--mobile-token-x': `${((position.column === 1 ? 0.8 : position.column === 11 ? 11.4 : position.column + 0.1) / 12.2) * 100}%`,
                    '--mobile-token-y': `${((position.row === 1 ? 1.3 : position.row === 11 ? 12.9 : position.row + 1.1) / 14.2) * 100}%`,
                    '--token-offset-x': `${(peerIndex % 2) * 18 - (peers.length > 1 ? 9 : 0)}%`,
                    '--token-offset-y': `${Math.floor(peerIndex / 2) * 18 - (peers.length > 2 ? 9 : 0)}%`,
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
